import { useContext, useEffect, useState } from 'react';
import {
    Autocomplete,
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    createFilterOptions,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { addingTag, assigningEmployeeToTag, gettingTags } from 'utils/Employees';
import { GlobalContext } from 'GlobalContext';
import { EmployeeService } from 'services/EmployeeService';

const filter = createFilterOptions();
const employeeService = new EmployeeService();

const AssignTag = ({ open, setOpen, data, checked, reloading }) => {
    const { t } = useTranslation();
    const { handlerAlert } = useContext(GlobalContext);

    // Tab activo: 0 = añadir nuevo rango, 1 = gestionar rangos existentes
    const [tab, setTab] = useState(0);

    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);
    const [overwrite, setOverwrite] = useState(false);
    const [loadingAssign, setLoadingAssign] = useState(false);
    const [alerts, setAlerts] = useState(false);

    // Rangos existentes para un único empleado seleccionado
    const [existingRanges, setExistingRanges] = useState([]);
    const [loadingRanges, setLoadingRanges] = useState(false);

    const singleEmployee = checked?.length === 1 ? checked[0] : null;

    const hasUnsavedChanges = () => {
        return selectedTags.length > 0 || start !== null || end !== null || overwrite;
    };

    const closing = (skipConfirm = false) => {
        if (!skipConfirm && hasUnsavedChanges()) {
            setAlerts(true);
            return;
        }

        setOpen(false);
        setLoading(false);
        setLoadingAssign(false);
        setTags([]);
        setSelectedTags([]);
        setStart(null);
        setEnd(null);
        setOverwrite(false);
        setExistingRanges([]);
        setTab(0);
        setAlerts(false);
    };

    // Carga catálogo de tags y rangos existentes al abrir
    useEffect(() => {
        if (!open) return;
        gettingTags(setLoading, setTags);
        if (singleEmployee) {
            loadExistingRanges(singleEmployee.employee_id);
        }
    }, [open]);

    const loadExistingRanges = async (employee_id) => {
        setLoadingRanges(true);
        try {
            const data = await employeeService.employee.getTagsByEmployee(employee_id);
            setExistingRanges(data ?? []);
        } catch (e) {
            console.error('Error loading existing ranges:', e);
            setExistingRanges([]);
        } finally {
            setLoadingRanges(false);
        }
    };

    const getTagName = (tag_id) => {
        const tag = tags.find((t) => t.id === tag_id);
        return tag ? tag.name : `Tag #${tag_id}`;
    };

    const handleAssign = () => {
        if (!start || !end || selectedTags.length === 0 || hasInvalidDateRange) return;
        assigningEmployeeToTag(
            setLoadingAssign,
            checked.map((row) => row.employee_id),
            selectedTags.map((tag) => tag.id),
            start.format('YYYY-MM-DD'),
            end.format('YYYY-MM-DD'),
            () => {
                // recargar rangos si es empleado único
                if (singleEmployee) {
                    loadExistingRanges(singleEmployee.employee_id);
                }
                setSelectedTags([]);
                setStart(null);
                setEnd(null);
            },
            reloading,
            handlerAlert,
            t,
            overwrite,
        );
    };

    const canAssign = JSON.parse(localStorage.getItem('permissions') ?? '[]')?.find((p) => p === 'employee.AssignEmployeeToTag');
    const hasInvalidDateRange = Boolean(start && end && end.isBefore(start, 'day'));

    return (
        <Dialog open={open} onClose={() => closing(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="body3">{t('timekeeper.employees.dialog.assign_tag.title')}</Typography>
            </DialogTitle>

            {alerts && (
                <Box sx={{ top: 0, zIndex: 10, pb: 1, px: 3, pt: 2 }}>
                    <Alert
                        variant="filled"
                        severity="warning"
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>
                                <Button onClick={() => closing(true)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                </Box>
            )}

            <DialogContent dividers sx={{ p: 0 }}>
                {/* Lista de empleados seleccionados */}
                <Box sx={{ px: 2, pt: 1, pb: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                        {t('timekeeper.employees.dialog.assign_tag.selected_employees', {
                            count: checked.length,
                        })}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                        {checked.map((row) => (
                            <Chip key={row.employee_id} size="small" label={`${row.document_employee} - ${row.first_name} ${row.last_name}`} />
                        ))}
                    </Stack>
                </Box>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, mt: 1 }} variant="fullWidth">
                    <Tab label={t('timekeeper.employees.dialog.assign_tag.tab_add')} />
                    {singleEmployee && <Tab label={t('timekeeper.employees.dialog.assign_tag.tab_manage')} />}
                </Tabs>

                <Divider />

                {/* TAB 0: Añadir nuevo rango */}
                {tab === 0 && (
                    <Box sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    multiple
                                    id="tags"
                                    value={selectedTags}
                                    options={tags.map((tag) => ({ ...tag, created: false }))}
                                    getOptionLabel={(option) => option.name}
                                    filterSelectedOptions
                                    onChange={(_, newValue) => setSelectedTags(newValue)}
                                    filterOptions={(options, params) => {
                                        const filtered = filter(options, params);
                                        const { inputValue } = params;
                                        const isExisting = options.some((o) => inputValue === o.name);
                                        if (inputValue !== '' && !isExisting) {
                                            filtered.push({ id: undefined, name: inputValue, created: true });
                                        }
                                        return filtered;
                                    }}
                                    renderInput={(params) => <TextField {...params} label={t('timekeeper.employees.dialog.assign_tag.tags')} />}
                                    renderOption={(props, option) => {
                                        const { key, ...optionProps } = props;
                                        return option.created ? (
                                            <li
                                                key={key}
                                                {...optionProps}
                                                onClick={async () => {
                                                    await addingTag(setLoading, option.name);
                                                    await gettingTags(setLoading, setTags);
                                                }}
                                            >
                                                {`Add "${option.name}"`}
                                            </li>
                                        ) : (
                                            <li key={key} {...optionProps}>
                                                {option.name}
                                            </li>
                                        );
                                    }}
                                    loading={loading}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid size={{ xs: 6 }}>
                                <DatePicker sx={{ width: '100%' }} label={t('timekeeper.employees.dialog.assign_tag.start_date')} value={start} onChange={setStart} />
                            </Grid>

                            <Grid size={{ xs: 6 }}>
                                <DatePicker sx={{ width: '100%' }} label={t('timekeeper.employees.dialog.assign_tag.end_date')} value={end} onChange={setEnd} />
                            </Grid>

                            {hasInvalidDateRange && (
                                <Grid size={{ xs: 12 }}>
                                    <Alert severity="error" sx={{ py: 0.5 }}>
                                        La fecha fin debe ser mayor o igual que la fecha inicio.
                                    </Alert>
                                </Grid>
                            )}

                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={<Checkbox checked={overwrite} onChange={(event) => setOverwrite(event.target.checked)} />}
                                    label="Overwrite: reemplaza asignaciones existentes de los empleados seleccionados"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* TAB 1: Gestionar rangos existentes (solo un empleado) */}
                {tab === 1 && singleEmployee && (
                    <Box sx={{ p: 2 }}>
                        {loadingRanges ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : existingRanges.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                                {t('timekeeper.employees.dialog.assign_tag.no_ranges')}
                            </Typography>
                        ) : (
                            <List dense disablePadding>
                                {existingRanges.map((range) => (
                                    <ListItem
                                        key={range.employee_r_tag_id ?? `${range.tag_id}-${range.start_date}`}
                                        secondaryAction={
                                            <Tooltip title={t('timekeeper.employees.dialog.assign_tag.delete_range')}>
                                                <span>
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        disabled={!canAssign}
                                                        onClick={async () => {
                                                            // Reasigna todos los rangos excepto el eliminado (overwrite)
                                                            const remaining = existingRanges.filter((r) => r.employee_r_tag_id !== range.employee_r_tag_id);
                                                            // Llama al endpoint con overwrite=true para reemplazar todos
                                                            try {
                                                                setLoadingRanges(true);
                                                                if (remaining.length > 0) {
                                                                    for (const r of remaining) {
                                                                        await employeeService.employee.assignTagsToEmployee(
                                                                            [singleEmployee.employee_id],
                                                                            [r.tag_id],
                                                                            r.start_date.split('T')[0],
                                                                            r.end_date.split('T')[0],
                                                                            false,
                                                                        );
                                                                    }
                                                                }
                                                                // El primer assign con overwrite borra todo y repone
                                                                // Para eliminar solo uno, se tiene que hacer overwrite + reinsertar el resto
                                                                // Enfoque alternativo: overwrite full
                                                                await employeeService.employee.assignTagsToEmployee(
                                                                    [singleEmployee.employee_id],
                                                                    remaining.map((r) => r.tag_id),
                                                                    remaining[0]?.start_date?.split('T')[0] ?? new Date().toISOString().split('T')[0],
                                                                    remaining[0]?.end_date?.split('T')[0] ?? new Date().toISOString().split('T')[0],
                                                                    true, // overwrite
                                                                );
                                                                await loadExistingRanges(singleEmployee.employee_id);
                                                                reloading();
                                                            } catch (e) {
                                                                console.error(e);
                                                            } finally {
                                                                setLoadingRanges(false);
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        }
                                    >
                                        <ListItemText primary={getTagName(range.tag_id)} secondary={`${range.start_date?.split('T')[0]} → ${range.end_date?.split('T')[0]}`} />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button variant="contained" disableElevation color="error" onClick={closing}>
                    {t('settings.paycodes.table.modals.add.buttons.cancel')}
                </Button>

                {tab === 0 && (
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<AddIcon />}
                        onClick={handleAssign}
                        loading={loadingAssign}
                        disabled={!canAssign || !start || !end || selectedTags.length === 0 || hasInvalidDateRange}
                    >
                        {t('settings.paycodes.table.modals.add.buttons.save')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export { AssignTag };
